import { useState, useEffect, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { useLangPrefix } from '../../hooks/useLangPrefix'
import { createContact } from '../../api/contacts'
import {
  PHONE_INPUT_MAX_LENGTH,
  PHONE_VALIDATION_MESSAGE,
  isValidPhoneNumber,
  normalizePhoneNumber,
  sanitizePhoneInput,
} from '../../utils/phone'

const INPUT_CLS = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:border-[#1535c8] transition-colors'

function FormField({ label, required, children }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-semibold text-gray-700 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  )
}

function StageRadio({ value, onChange }) {
  return (
    <div className="flex gap-6">
      {[{ v: 'follower', label: 'Follower' }, { v: 'customer', label: 'Customer' }].map(({ v, label }) => (
        <label key={v} className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="add-contact-stage"
            value={v}
            checked={value === v}
            onChange={() => onChange(v)}
            className="accent-[#1535c8]"
          />
          <span className="text-sm text-gray-700">{label}</span>
        </label>
      ))}
    </div>
  )
}

export default function AddContactModal({ open, onClose, tenantId, defaultStage, onCreated }) {
  const navigate = useNavigate()
  const { prefix } = useLangPrefix()

  const [name, setName]       = useState('')
  const [phone, setPhone]     = useState('')
  const [email, setEmail]     = useState('')
  const [address, setAddress] = useState('')
  const [street, setStreet]     = useState('')
  const [ward, setWard]         = useState('')
  const [district, setDistrict] = useState('')
  const [province, setProvince] = useState('')
  const [latitude, setLatitude]   = useState('')
  const [longitude, setLongitude] = useState('')
  const [stage, setStage]     = useState(defaultStage || 'follower')
  const [notes, setNotes]     = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  const [dupId, setDupId]     = useState(null)
  const [phoneError, setPhoneError] = useState('')
  const isCustomerStage = stage === 'customer'

  useEffect(() => {
    if (open) {
      setName(''); setPhone(''); setEmail(''); setAddress('')
      setStreet(''); setWard(''); setDistrict(''); setProvince('')
      setLatitude(''); setLongitude('')
      setStage(defaultStage || 'follower')
      setNotes(''); setError(null); setDupId(null); setPhoneError('')
    }
  }, [open, defaultStage])

  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    const cleanPhone = normalizePhoneNumber(phone)
    if (isCustomerStage && !cleanPhone) {
      setPhoneError('Phone is required for customer stage.')
      return
    }
    if (cleanPhone && !isValidPhoneNumber(cleanPhone)) {
      setPhoneError(PHONE_VALIDATION_MESSAGE)
      return
    }
    setLoading(true)
    setError(null)
    setDupId(null)
    try {
      const body = {
        name: name.trim(),
        ...(cleanPhone && { phone: cleanPhone }),
        ...(email.trim()   && { email:   email.trim() }),
        ...(address.trim()  && { address:  address.trim() }),
        ...(street.trim()   && { street:   street.trim() }),
        ...(ward.trim()     && { ward:     ward.trim() }),
        ...(district.trim() && { district: district.trim() }),
        ...(province.trim() && { province: province.trim() }),
        ...(latitude.trim()  && { latitude:  Number(latitude) }),
        ...(longitude.trim() && { longitude: Number(longitude) }),
        stage,
        ...(notes.trim()   && { notes:   notes.trim() }),
      }
      const newContact = await createContact(tenantId, body)
      onCreated(newContact)
      onClose()
    } catch (err) {
      if (err.response?.status === 409) {
        setDupId(err.response.data?.contact_id || null)
        setError('A contact with this phone/email already exists.')
      } else {
        Sentry.captureException(err, {
          tags: { action: 'create_contact' },
          extra: { tenantId, stage },
        })
        setError(err.response?.data?.message || 'Something went wrong. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }, [name, phone, email, address, street, ward, district, province, latitude, longitude, stage, notes, tenantId, onCreated, onClose, isCustomerStage])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <span className="font-bold text-gray-900 text-base">Add Contact</span>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 w-7 h-7 flex items-center justify-center text-xl leading-none rounded-full hover:bg-gray-100 transition-colors"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4">
          <FormField label="Name" required>
            <input
              className={INPUT_CLS}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Full name"
              autoFocus
              required
            />
          </FormField>

          <FormField label="Phone" required={isCustomerStage}>
            <input
              className={INPUT_CLS}
              value={phone}
              onChange={e => {
                const cleanValue = sanitizePhoneInput(e.target.value)
                const normalizedPhone = normalizePhoneNumber(cleanValue)
                setPhone(cleanValue)
                if (!normalizedPhone) {
                  setPhoneError(isCustomerStage ? 'Phone is required for customer stage.' : '')
                } else if (isValidPhoneNumber(normalizedPhone)) {
                  setPhoneError('')
                } else {
                  setPhoneError(PHONE_VALIDATION_MESSAGE)
                }
              }}
              placeholder="Phone number"
              type="tel"
              inputMode="tel"
              maxLength={PHONE_INPUT_MAX_LENGTH}
              required={isCustomerStage}
            />
            {phoneError && <p className="text-xs text-red-600 mt-1">{phoneError}</p>}
          </FormField>

          <FormField label="Email">
            <input
              className={INPUT_CLS}
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email address"
              type="email"
            />
          </FormField>

          <FormField label="Address">
            <input
              className={INPUT_CLS}
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="Address"
            />
          </FormField>

          <FormField label="Street">
            <input
              className={INPUT_CLS}
              value={street}
              onChange={e => setStreet(e.target.value)}
              placeholder="Street / house number"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Ward">
              <input
                className={INPUT_CLS}
                value={ward}
                onChange={e => setWard(e.target.value)}
                placeholder="Ward"
              />
            </FormField>
            <FormField label="District">
              <input
                className={INPUT_CLS}
                value={district}
                onChange={e => setDistrict(e.target.value)}
                placeholder="District"
              />
            </FormField>
          </div>

          <FormField label="Province/City">
            <input
              className={INPUT_CLS}
              value={province}
              onChange={e => setProvince(e.target.value)}
              placeholder="Province / City"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Latitude">
              <input
                className={INPUT_CLS}
                value={latitude}
                onChange={e => setLatitude(e.target.value)}
                placeholder="e.g. 10.762622"
                inputMode="decimal"
              />
            </FormField>
            <FormField label="Longitude">
              <input
                className={INPUT_CLS}
                value={longitude}
                onChange={e => setLongitude(e.target.value)}
                placeholder="e.g. 106.660172"
                inputMode="decimal"
              />
            </FormField>
          </div>

          <FormField label="Stage">
            <StageRadio
              value={stage}
              onChange={(nextStage) => {
                setStage(nextStage)
                const cleanPhone = normalizePhoneNumber(phone)
                if (!cleanPhone) {
                  setPhoneError(nextStage === 'customer' ? 'Phone is required for customer stage.' : '')
                } else if (!isValidPhoneNumber(cleanPhone)) {
                  setPhoneError(PHONE_VALIDATION_MESSAGE)
                } else {
                  setPhoneError('')
                }
              }}
            />
          </FormField>

          <FormField label="Notes">
            <textarea
              className={`${INPUT_CLS} resize-none`}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Internal notes"
              rows={3}
            />
          </FormField>

          {error && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
              {dupId && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="underline font-semibold"
                    onClick={() => { onClose(); navigate(`${prefix}/followers/${dupId}`) }}
                  >
                    View existing
                  </button>
                </>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !name.trim() || Boolean(phoneError)}
            className="w-full bg-[#1535c8] text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {loading ? 'Saving…' : 'Add Contact'}
          </button>
        </form>
      </div>
    </div>
  )
}
